import { SerializedStyles } from "@emotion/react";
import { ReactNode, Ref, memo, useCallback } from "react";
import { FormFlowPage, InfoAlert, LabeledSlider } from "src/components";
import { useWindowSize } from "src/hooks";
import { createStyles } from "src/styles";

import { colors, formatters, isTruthy, utilities } from "@fraction/shared";

import { useNoSubmit } from "../hooks";
import { styles } from "../styles";

const formatter = (val: number) =>
  formatters.number.getCurrencyWords(val, {
    translations: [
      {
        divisor: 1000000000,
        suffix: " billion",
        remainder: 1,
      },
      {
        divisor: 1000000,
        suffix: " million",
        remainder: 1,
        digits: 2,
      },
    ],
    digits: 0,
  });

export interface LoanAmountRequestedProps {
  onChange: (amount: number) => void;
  value: number;
  sliderMin: number;
  sliderMax: number;
  sliderStep: number;
  homeValue: number;
  maximumLTV: number;

  focusRef?: Ref<HTMLDivElement>;
  onNext: () => void;
  onBack: () => void;
  style?: SerializedStyles;
  children?: ReactNode;
  existingMortgageAmount?: number;
}

const INNER_STYLES = createStyles({
  unbreakableWord: {
    whiteSpace: "nowrap",
    display: "inline",
    margin: 0,
  },
  tinyLabel: {
    margin: 0,
    fontSize: 8,
    width: "min(25vw, 100px)",
    textAlign: "center",
  },
  noMargin: {
    margin: 0,
  },
  leftAlignedLabel: {
    textAlign: "left",
    transform: "translateX(37.5%)",
  },
  rightAlignedLabel: {
    textAlign: "right",
    transform: "translateX(-37.5%)",
  },
  hardLeftAlignedLabel: {
    textAlign: "left",
    transform: "translateX(45%)",
  },
  hardRightAlignedLabel: {
    textAlign: "right",
    transform: "translateX(-45%)",
  },
  leftAlignedTinyLabel: {
    textAlign: "left",
  },
  rightAlignedTinyLabel: {
    textAlign: "right",
  },
});

// left align the label if less than x% of the slider (gets too crowded)
const LEFT_ALIGN_LABEL_DISTANCE_PERCENTAGE = 10;
const HARD_LEFT_ALIGNED_LABEL_DISTANCE_PERCENTAGE = 3;
const HIDE_MAX_LABEL_DISTANCE_PERCENTAGE = 90;

const LoanAmountRequested = ({
  value,
  homeValue,
  onChange,
  onNext,
  onBack,
  style,
  focusRef,
  sliderMin,
  sliderMax,
  sliderStep,
  existingMortgageAmount,
  children,
  maximumLTV,
  ...props
}: LoanAmountRequestedProps) => {
  const validate = useCallback(() => value !== undefined, [value]);
  const { handleNext, nextDisabled } = useNoSubmit({ onNext, validate });
  const handleNextFinal = useCallback(
    (event: React.ChangeEvent<HTMLFormElement>) => {
      event.preventDefault();
      onChange(value);
      handleNext();
    },
    [onChange, value, handleNext]
  );

  const maxAmount = Math.ceil(sliderMax / sliderStep) * sliderStep;
  const existingMortgageBody = existingMortgageAmount ? (
    <>
      Of this,{" "}
      <b css={INNER_STYLES.unbreakableWord}>{formatters.number.getCurrencyWords(existingMortgageAmount)}</b>{" "}
      will go towards paying off your existing mortgage - the rest can be used any way you'd like.
    </>
  ) : (
    ""
  );

  const body =
    sliderMax === 150000000 ? (
      <p>
        Fraction can help you unlock up to <b>$1.5M</b> of your{" "}
        <b css={INNER_STYLES.unbreakableWord}>{formatters.number.getCurrencyWords(homeValue)}</b> home.{" "}
        {existingMortgageBody}
      </p>
    ) : (
      <p>
        Fraction can help you unlock up to <b>{formatters.number.getCurrencyWords(maxAmount)}</b>, which is{" "}
        {Math.round(maximumLTV * 100)}% of your{" "}
        <b css={INNER_STYLES.unbreakableWord}>{formatters.number.getCurrencyWords(homeValue)}</b> home.{" "}
        {existingMortgageBody}
      </p>
    );

  const { width } = useWindowSize();
  const isMobile = !!width && width < 480;

  const boundedPercent = utilities.number.boundedPercentage(
    existingMortgageAmount || 0,
    sliderMin,
    sliderMax
  );

  const showInaccessibleRegion = !!existingMortgageAmount && existingMortgageAmount > sliderMin;

  const hideMinLabel = showInaccessibleRegion;
  const hideMaxLabel = !!existingMortgageAmount && boundedPercent > HIDE_MAX_LABEL_DISTANCE_PERCENTAGE;

  const leftAlignLabel = hideMinLabel && isMobile && boundedPercent <= LEFT_ALIGN_LABEL_DISTANCE_PERCENTAGE;
  const hardLeftAlignLabel = leftAlignLabel && boundedPercent <= HARD_LEFT_ALIGNED_LABEL_DISTANCE_PERCENTAGE;

  const rightAlignLabel =
    hideMinLabel && isMobile && boundedPercent > 100 - LEFT_ALIGN_LABEL_DISTANCE_PERCENTAGE;
  const hardRightAlignLabel = leftAlignLabel && boundedPercent <= HARD_LEFT_ALIGNED_LABEL_DISTANCE_PERCENTAGE;

  const labelStyle = hardLeftAlignLabel
    ? INNER_STYLES.hardLeftAlignedLabel
    : hardRightAlignLabel
    ? INNER_STYLES.hardRightAlignedLabel
    : leftAlignLabel
    ? INNER_STYLES.leftAlignedLabel
    : rightAlignLabel
    ? INNER_STYLES.rightAlignedLabel
    : undefined;

  return (
    <FormFlowPage
      {...props}
      title="How much would you like from Fraction?"
      description={body}
      onNext={handleNextFinal}
      onBack={onBack}
      style={style}
      nextDisabled={nextDisabled}
      ref={focusRef}
    >
      <LabeledSlider
        style={styles.slider}
        onChange={onChange}
        value={value}
        formatter={formatter}
        min={sliderMin}
        max={maxAmount}
        hideMin={hideMinLabel}
        hideMax={hideMaxLabel}
        step={sliderStep}
        inaccessibleRegions={[
          showInaccessibleRegion && {
            color: colors.palette.GREEN_650,
            value: existingMortgageAmount || 0,
            labelStyle: [INNER_STYLES.noMargin, labelStyle],
            label: (
              <>
                <p css={INNER_STYLES.unbreakableWord}>
                  {formatters.number.getCurrencyWords(existingMortgageAmount)}
                </p>
                <p
                  css={[
                    INNER_STYLES.tinyLabel,
                    leftAlignLabel && INNER_STYLES.leftAlignedTinyLabel,
                    rightAlignLabel && INNER_STYLES.rightAlignedTinyLabel,
                  ]}
                >
                  towards your existing mortgage
                </p>
              </>
            ),
          },
        ].filter(isTruthy)}
      />
      {children}
      {!!existingMortgageAmount && (
        <InfoAlert style={styles.alert}>
          Fraction is a first-lien product, which means that you will be required to pay off your existing
          mortgage(s) of{" "}
          <p css={INNER_STYLES.unbreakableWord}>
            {formatters.number.getCurrencyWords(existingMortgageAmount)}
          </p>
          . On the plus side? This means <b>no more monthly payments!</b>
        </InfoAlert>
      )}
    </FormFlowPage>
  );
};

export default memo(LoanAmountRequested);
